import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';
import { favoritesService, FAVORITES_LIMIT } from '../../services/favorites.service';
import { errorMessage } from '../../lib/apiErrors';
import { FLAG, getFeatureFlag } from '../../hooks/useFeatureFlag';

/**
 * Favorites — saved archive posts (Pinterest-style save surface).
 *
 * Storage strategy:
 *   • Guest visitors: localStorage only. Lets a guest collect a small set
 *     before signup so we don't lose intent at the auth wall.
 *   • Authed users with `wishlist_sync` flag ON: server is authoritative.
 *     Local cache is still written for instant cold-load, but every toggle
 *     fires the backend and the server response is the canonical state.
 *   • Authed users with the flag OFF: same as guest (local-only). The
 *     /v1/favorites endpoints exist but the FE keeps the legacy behavior
 *     until the flag is enabled per-user / globally.
 *
 * Sync-on-login: when a guest signs in, `syncFavoritesOnAuth` POSTs the
 * local list to /v1/favorites/me/sync, then re-fetches the server list and
 * replaces local state. Idempotent — safe to call on every auth transition.
 */

const STORAGE_KEY = 'ca_favorites_v1';

export interface FavoriteEntry {
  id: string; // archive post id
  addedAt: string; // ISO timestamp
}

export interface FavoritesState {
  posts: FavoriteEntry[];
  hydrated: boolean;
  /** True while a network request (fetch / sync) is in flight. */
  syncing: boolean;
  /** Last successful server sync (ISO). Null until first server response. */
  lastSyncedAt: string | null;
  /** Last error surfaced by a thunk; cleared on success. */
  error: string | null;
}

const initialState: FavoritesState = {
  posts: [],
  hydrated: false,
  syncing: false,
  lastSyncedAt: null,
  error: null,
};

const loadFromStorage = (): Pick<FavoritesState, 'posts'> => {
  if (typeof window === 'undefined') return { posts: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { posts: [] };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.posts)) return { posts: parsed.posts };
    return { posts: [] };
  } catch {
    return { posts: [] };
  }
};

const persist = (state: FavoritesState) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ posts: state.posts }));
  } catch {
    /* quota / disabled storage — safe to ignore */
  }
};

/* ---------- Thunks ---------- */

/**
 * Fetch the authoritative server list. Replaces local state on success.
 * Caller must ensure the user is authed and `wishlist_sync` is on; the thunk
 * does not gate itself so callers can opt-in/out at the call site.
 */
export const fetchFavorites = createAsyncThunk<
  FavoriteEntry[],
  void,
  { rejectValue: string }
>('favorites/fetch', async (_, { rejectWithValue }) => {
  try {
    return await favoritesService.list();
  } catch (e) {
    return rejectWithValue(errorMessage(e, 'Failed to load favorites'));
  }
});

/**
 * Toggle a post in favorites. Behavior depends on the user's `wishlist_sync`
 * flag (read from getState at call time):
 *   • Flag ON + authed: optimistic update + server POST/DELETE; rollback on
 *     failure.
 *   • Otherwise: local-only mutation, persisted to localStorage. The server
 *     is never touched.
 */
export const toggleFavorite = createAsyncThunk<
  { id: string; nowFavorited: boolean },
  string,
  { state: RootState; rejectValue: string }
>('favorites/toggle', async (postId, { getState, dispatch, rejectWithValue }) => {
  const state = getState();
  const isAuthed = state.auth.isAuthenticated;
  const syncEnabled = getFeatureFlag(state.auth.user, FLAG.WISHLIST_SYNC);
  const wasFavorited = state.favorites.posts.some((p) => p.id === postId);
  const nowFavorited = !wasFavorited;

  // Optimistic update first — UI is responsive whether we sync or not.
  dispatch(favoritesSlice.actions._togglePostLocal(postId));

  if (!isAuthed || !syncEnabled) {
    return { id: postId, nowFavorited };
  }

  try {
    if (nowFavorited) {
      const entry = await favoritesService.add(postId);
      // Reconcile addedAt with the server-issued timestamp so subsequent
      // sorts use the canonical value.
      dispatch(favoritesSlice.actions._reconcileEntry(entry));
    } else {
      await favoritesService.remove(postId);
    }
    return { id: postId, nowFavorited };
  } catch (e) {
    // Rollback the optimistic mutation so local state matches server.
    dispatch(favoritesSlice.actions._togglePostLocal(postId));
    return rejectWithValue(errorMessage(e, 'Failed to update favorite'));
  }
});

/**
 * Push the current local set to the server, then re-fetch to lock in the
 * canonical state. Designed to run exactly once per auth transition.
 * No-op when there's nothing to sync or the flag is off.
 */
export const syncFavoritesOnAuth = createAsyncThunk<
  FavoriteEntry[],
  void,
  { state: RootState; rejectValue: string }
>('favorites/syncOnAuth', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  if (!state.auth.isAuthenticated) return state.favorites.posts;
  if (!getFeatureFlag(state.auth.user, FLAG.WISHLIST_SYNC)) {
    return state.favorites.posts;
  }

  try {
    const localIds = state.favorites.posts.slice(0, FAVORITES_LIMIT).map((p) => p.id);
    if (localIds.length > 0) {
      await favoritesService.sync(localIds);
    }
    // Always re-fetch so we replace local state with whatever the server
    // says — including entries that other devices may have added.
    return await favoritesService.list();
  } catch (e) {
    return rejectWithValue(errorMessage(e, 'Failed to sync favorites'));
  }
});

/* ---------- Slice ---------- */

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    hydrateFavorites: (state) => {
      const { posts } = loadFromStorage();
      state.posts = posts;
      state.hydrated = true;
    },
    /**
     * @deprecated Prefer the `toggleFavorite` thunk so server sync runs when
     * the `wishlist_sync` flag is enabled. Kept exported for any legacy call
     * sites that haven't migrated yet — internally it just mutates local
     * state with no network round-trip.
     */
    togglePost: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.posts.findIndex((p) => p.id === id);
      if (idx >= 0) state.posts.splice(idx, 1);
      else state.posts.unshift({ id, addedAt: new Date().toISOString() });
      persist(state);
    },
    /** Internal: optimistic add/remove used by the `toggleFavorite` thunk. */
    _togglePostLocal: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.posts.findIndex((p) => p.id === id);
      if (idx >= 0) state.posts.splice(idx, 1);
      else state.posts.unshift({ id, addedAt: new Date().toISOString() });
      persist(state);
    },
    /** Internal: replace a local entry's `addedAt` with the server's value. */
    _reconcileEntry: (state, action: PayloadAction<FavoriteEntry>) => {
      const idx = state.posts.findIndex((p) => p.id === action.payload.id);
      if (idx >= 0) state.posts[idx] = action.payload;
      persist(state);
    },
    clearFavorites: (state) => {
      state.posts = [];
      persist(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFavorites.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(fetchFavorites.fulfilled, (state, action) => {
        state.syncing = false;
        state.posts = action.payload;
        state.lastSyncedAt = new Date().toISOString();
        persist(state);
      })
      .addCase(fetchFavorites.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload ?? 'Failed to load favorites';
      })
      .addCase(syncFavoritesOnAuth.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncFavoritesOnAuth.fulfilled, (state, action) => {
        state.syncing = false;
        state.posts = action.payload;
        state.lastSyncedAt = new Date().toISOString();
        persist(state);
      })
      .addCase(syncFavoritesOnAuth.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload ?? 'Failed to sync favorites';
      });
  },
});

export const { hydrateFavorites, togglePost, clearFavorites } = favoritesSlice.actions;

export default favoritesSlice.reducer;

// Selectors
export const selectFavoritePosts = (s: RootState) => s.favorites.posts;
export const selectFavoritesSyncing = (s: RootState) => s.favorites.syncing;
export const selectFavoritesError = (s: RootState) => s.favorites.error;
export const selectIsPostFavorited = (id: string) => (s: RootState) =>
  s.favorites.posts.some((p) => p.id === id);
